import { Link, usePage } from '@inertiajs/react'
import { Bot, ChartNoAxesCombined, ClipboardCheck, Landmark, ListChecks, LogOut, MoreHorizontal, PanelLeftClose, PanelLeftOpen, ReceiptText, SearchCheck, WalletCards, WalletMinimal } from 'lucide-react'
import { useState } from 'react'
import { BudgetLogo } from '@/components/budget-logo'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'transactions.sidebarCollapsed'

type SharedProps = {
  auth: {
    user: { id: number; email: string; admin: boolean } | null
  }
}

const navItems = [
  { href: '/accounts', label: 'Accounts', icon: WalletCards, section: '/accounts' },
  { href: '/budgets', label: 'Budgets', icon: WalletMinimal, section: '/budgets' },
  { href: '/wealth', label: 'Wealth', icon: ChartNoAxesCombined, section: '/wealth' },
  { href: '/tax-reserve', label: 'Tax Reserve', icon: Landmark, section: '/tax-reserve' },
  { href: '/reviews', label: 'Reviews', icon: ClipboardCheck, section: '/reviews' },
  { href: '/transactions', label: 'Transactions', icon: ListChecks, section: '/transactions' },
  { href: '/copilot', label: 'Copilot', icon: Bot, section: '/copilot' },
]

const secondaryNavItems = [
  { href: '/spends', label: 'Spends', icon: ReceiptText, section: '/spends' },
  { href: '/matchers', label: 'Matchers', icon: SearchCheck, section: '/matchers' },
]

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { auth } = usePage<SharedProps>().props
  const path = window.location.pathname
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true'
  })

  function toggleSidebarCollapsed() {
    setSidebarCollapsed((collapsed) => {
      const nextCollapsed = !collapsed
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(nextCollapsed))
      return nextCollapsed
    })
  }

  if (!auth.user) {
    return <main className="min-h-screen">{children}</main>
  }

  return (
    <div className={cn('min-h-screen bg-muted/20 lg:grid', sidebarCollapsed ? 'lg:grid-cols-[5.5rem_minmax(0,1fr)]' : 'lg:grid-cols-[13.5rem_minmax(0,1fr)]')}>
      <aside className={cn('sticky top-0 hidden h-screen border-r bg-background/95 py-5 shadow-sm transition-[width] lg:flex lg:flex-col', sidebarCollapsed ? 'px-3' : 'px-4')}>
        <TooltipProvider delayDuration={150}>
        <div className={cn('mb-8 flex items-center gap-3', sidebarCollapsed ? 'flex-col justify-center' : 'justify-between')}>
          <Link href="/" className={cn('flex items-center gap-3 text-2xl font-bold tracking-tight', sidebarCollapsed && 'justify-center')}>
            <BudgetLogo className="h-11 w-11 shrink-0 drop-shadow-sm" />
            <span className={cn(sidebarCollapsed && 'sr-only')}>Budget</span>
          </Link>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={toggleSidebarCollapsed}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">{sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}</TooltipContent>
          </Tooltip>
        </div>

        <nav className="space-y-1">
          {[...navItems, ...secondaryNavItems].map((item) => {
            const Icon = item.icon
            const active = path.startsWith(item.section)

            return (
              <Tooltip key={item.href}>
                <TooltipTrigger asChild>
                  <Link
                    href={item.href}
                    prefetch
                    className={cn(
                      'flex h-11 items-center gap-3 rounded-md text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                      sidebarCollapsed ? 'justify-center px-0' : 'px-3',
                      active && 'bg-primary/10 text-primary shadow-sm hover:bg-primary/10 hover:text-primary',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span className={cn(sidebarCollapsed && 'sr-only')}>{item.label}</span>
                  </Link>
                </TooltipTrigger>
                {sidebarCollapsed ? <TooltipContent side="right">{item.label}</TooltipContent> : null}
              </Tooltip>
            )
          })}
        </nav>

        <div className="mt-auto border-t pt-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                variant="ghost"
                size={sidebarCollapsed ? 'icon' : 'default'}
                className={cn('text-muted-foreground hover:text-foreground', sidebarCollapsed ? 'mx-auto flex' : 'w-full justify-start')}
              >
                <Link href="/logout">
                  <LogOut className="h-4 w-4" />
                  <span className={cn(sidebarCollapsed && 'sr-only')}>Log Out</span>
                </Link>
              </Button>
            </TooltipTrigger>
            {sidebarCollapsed ? <TooltipContent side="right">Log Out</TooltipContent> : null}
          </Tooltip>
        </div>
        </TooltipProvider>
      </aside>

      <header className="sticky top-0 z-20 border-b bg-background/90 shadow-sm backdrop-blur lg:hidden">
        <div className="flex h-16 items-center gap-3 px-4">
          <Link href="/" className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <BudgetLogo className="h-10 w-10 drop-shadow-sm" />
            Budget
          </Link>
          <nav className="flex flex-1 items-center justify-end gap-1">
            {navItems.slice(0, 2).map((item) => {
              const Icon = item.icon
              const active = path.startsWith(item.section)

              return (
                <Button
                  key={item.href}
                  asChild
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'text-muted-foreground hover:text-foreground',
                    active && 'bg-primary/10 text-primary shadow-sm hover:bg-primary/10 hover:text-primary',
                  )}
                >
                  <Link href={item.href} prefetch aria-label={item.label}>
                    <Icon className="h-4 w-4" />
                  </Link>
                </Button>
              )
            })}
          </nav>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'text-muted-foreground hover:text-foreground',
                  [...navItems.slice(2), ...secondaryNavItems].some((item) => path.startsWith(item.section)) &&
                    'bg-primary/10 text-primary shadow-sm hover:bg-primary/10 hover:text-primary',
                )}
                aria-label="More navigation"
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuGroup>
                {[...navItems.slice(2), ...secondaryNavItems].map((item) => {
                  const Icon = item.icon
                  const active = path.startsWith(item.section)

                  return (
                    <DropdownMenuItem
                      key={item.href}
                      asChild
                      className={cn(
                        'text-muted-foreground',
                        active && 'bg-primary/10 text-primary focus:bg-primary/10 focus:text-primary',
                      )}
                    >
                      <Link href={item.href} prefetch>
                        <Icon />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  )
                })}
                <DropdownMenuItem asChild className="text-muted-foreground">
                  <Link href="/logout">
                    <LogOut />
                    Log Out
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="min-w-0 px-4 py-6 sm:px-6 lg:px-8 lg:py-8 xl:px-10">{children}</main>
    </div>
  )
}
